import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '@/services/config';
import { buildQueryString, prepareHeaders } from '@/state/slices/index';
import type { Study } from '@/models/study';
import type {
  UploadInitRequest,
  UploadInitResponse,
  UploadListParams,
  UploadsListResult,
  UploadsSummary,
} from '@/models/upload';

export const UploadAPI = createApi({
  reducerPath: 'UploadAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: config.BASE_URL,
    prepareHeaders,
  }),
  tagTypes: ['Uploads'],
  endpoints: (builder) => ({
    listUploads: builder.query<UploadsListResult, UploadListParams | void>({
      query: (params) => ({
        url: `uploads/${buildQueryString(params ?? {})}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((upload) => ({
                type: 'Uploads' as const,
                id: upload.uuid,
              })),
              { type: 'Uploads' as const, id: 'LIST' },
            ]
          : [{ type: 'Uploads' as const, id: 'LIST' }],
    }),

    getUploadsSummary: builder.query<UploadsSummary, void>({
      query: () => ({ url: 'uploads/summary/', method: 'GET' }),
      providesTags: [{ type: 'Uploads' as const, id: 'SUMMARY' }],
    }),

    initUpload: builder.mutation<UploadInitResponse, UploadInitRequest>({
      query: (body) => ({
        url: 'uploads/init/',
        method: 'POST',
        body,
      }),
      // No invalidation here — the list and summary are refetched once the
      // GCS PUT completes (see UploadsPage); the row comes from the server.
    }),

    // POST /uploads/<uploadId>/complete/ — records when the file finished
    // sending (answers 204). No invalidation: UploadsPage refetches the list
    // and summary once the dialog reports success.
    completeUpload: builder.mutation<void, string>({
      query: (uploadId) => ({
        url: `uploads/${uploadId}/complete/`,
        method: 'POST',
      }),
    }),

    // PATCH /studies/<uuid>/ — re-points Study.patient. Used by the
    // AssignPatientModal (Link existing / Create new) on the Uploads page.
    // Lives here (not StudiesAPI) so a successful swap auto-invalidates
    // the Uploads list and the row flips back to a normal patient state.
    assignStudyPatient: builder.mutation<
      Study,
      { studyUuid: string; patientUuid: string }
    >({
      query: ({ studyUuid, patientUuid }) => ({
        url: `studies/${studyUuid}/`,
        method: 'PATCH',
        body: { patientUuid },
      }),
      invalidatesTags: (_result, _error, { studyUuid }) => [
        { type: 'Uploads', id: studyUuid },
        { type: 'Uploads', id: 'LIST' },
        { type: 'Uploads', id: 'SUMMARY' },
      ],
    }),
  }),
});

export const {
  useListUploadsQuery,
  useGetUploadsSummaryQuery,
  useInitUploadMutation,
  useCompleteUploadMutation,
  useAssignStudyPatientMutation,
} = UploadAPI;
